import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

export interface NavItem {
  /** メニュー項目のラベル */
  label: string;
  /** リンク先パス */
  path: string;
  /** Material Symbolsのアイコン名 */
  icon: string;
  /** 外部リンクかどうか */
  external?: boolean;
}

export interface NavGroup {
  /** グループのヘッダー */
  header?: string;
  /** メニュー項目 */
  items: NavItem[];
}

export interface SideMenuProps {
  /** サービスタイトル */
  title?: string;
  /** ナビゲーショングループ */
  navigation: NavGroup[];
  /** 追加のクラス名 */
  className?: string;
}

/**
 * AIPデザインシステムのサイドメニューコンポーネント
 */
export const SideMenu: React.FC<SideMenuProps> = ({
  title = 'AiiPo Management',
  navigation,
  className = '',
}) => {
  const location = useLocation();
  const { user, logout } = useAuth();

  // 現在のパスがアクティブかどうかを判定
  const isActive = (path: string) => {
    if (path === '/meeting/manage-new') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  // ユーザー名の頭文字を取得
  const getInitial = (name?: string, email?: string) => {
    if (name) return name.charAt(0);
    if (email) return email.charAt(0).toUpperCase();
    return '?';
  };

  return (
    <div className={`aip-side-menu ${className}`.trim()}>
      {/* ヘッダー */}
      <div className="aip-side-menu__header">
        <h2 className="aip-side-menu__title">{title}</h2>
      </div>

      {/* ナビゲーション */}
      <nav className="aip-side-menu__nav">
        {navigation.map((group, groupIndex) => (
          <React.Fragment key={groupIndex}>
            {group.header && (
              <div className="aip-side-menu__nav-header">{group.header}</div>
            )}
            <ul className="aip-side-menu__nav-list">
              {group.items.map((item) => (
                <li key={item.path} className="aip-side-menu__nav-item">
                  {item.external ? (
                    <a
                      href={item.path}
                      className="aip-side-menu__nav-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span className="material-symbols-outlined aip-side-menu__nav-icon">
                        {item.icon}
                      </span>
                      <span className="aip-side-menu__nav-text">{item.label}</span>
                      <span
                        className="material-symbols-outlined aip-side-menu__nav-icon"
                        data-style="description"
                      >
                        open_in_new
                      </span>
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className="aip-side-menu__nav-link"
                      data-active={isActive(item.path) || undefined}
                    >
                      <span className="material-symbols-outlined aip-side-menu__nav-icon">
                        {item.icon}
                      </span>
                      <span className="aip-side-menu__nav-text">{item.label}</span>
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          </React.Fragment>
        ))}
      </nav>

      {/* ユーザープロフィール */}
      {user && (
        <div className="aip-side-menu__profile" onClick={logout} title="ログアウト">
          <div className="aip-side-menu__profile-avatar">
            {getInitial(user.name, user.email)}
          </div>
          <div className="aip-side-menu__profile-info">
            <div className="aip-side-menu__profile-name">
              {user.name || user.email}
            </div>
            <div className="aip-side-menu__profile-role">
              {user.role || '管理者'}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SideMenu;

